import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Divider, Chip } from '@mui/material';
import PhoneInput from '../common/PhoneInput';

/**
 * Modal to capture a phone number for a participant.
 * - For temps without phone: shows "We need a phone for {name}".
 * - For employees with missing phone: same.
 * Calls onConfirm(digits) when user submits a valid 10-digit number.
 *
 * `history` (optional) — past changes of this person's number (who / from → to / when),
 * so an edit can always be traced back. Empty or omitted → block is hidden.
 */
export default function TempPhoneModal({
    open, onClose, onConfirm, participantName, initialPhone = '', kind = 'temp', history = [],
}) {
    const [phone, setPhone] = useState(initialPhone || '');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setPhone(initialPhone || '');
            setError(null);
        }
    }, [open, initialPhone]);

    const formatWhen = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
    };

    const handleConfirm = () => {
        if (phone.length !== 10) {
            setError('Please enter a complete 10-digit phone number');
            return;
        }
        onConfirm(phone);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>Phone number required</DialogTitle>
            <DialogContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {kind === 'temp'
                        ? `Enter a phone number for ${participantName}. It will be saved for future sessions.`
                        : `${participantName} has no phone on file. Please provide one to send the SMS.`}
                </Typography>
                <Box mt={1}>
                    <PhoneInput
                        value={phone}
                        onChange={(digits) => { setPhone(digits); setError(null); }}
                        autoFocus
                        error={error}
                    />
                </Box>

                {history.length > 0 && (
                    <Box mt={2.5}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Change history
                        </Typography>
                        <Box sx={{ mt: 1, maxHeight: 190, overflowY: 'auto' }}>
                            {history.map((h) => (
                                <Box key={h.id} sx={{ py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" sx={{ color: 'text.disabled', textDecoration: 'line-through' }}>
                                            {h.oldPhone || '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">→</Typography>
                                        <Typography variant="body2" fontWeight={600}>{h.newPhone || '—'}</Typography>
                                        {h.externalSynced === false && (
                                            <Chip label="not in external DB" size="small"
                                                sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#fef3c7', color: '#92400e' }} />
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {h.changedBy || 'Unknown'} · {formatWhen(h.changedAt)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleConfirm}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
